import {
  IElasticEventData,
  IStructuredLogger
} from "../../../../common/interfaces/structured-logging-service-interface";
import ServiceFactory from "../service_factory";
import BaseObject from "../../base/base_object";
import {EventType} from './interfaces/log-event-header.interface';
import {LogEventHeaderBuilder} from './builders/log-event-header-builder';
import {LogEventDataBuilder} from './builders/log-event-data-builder';
import {ILogRequestPayload} from './interfaces/log-request-payload.interface';
import {LogDeviceDataBuilder} from './builders/log-device-data-builder';
import {LogClientDataBuilder} from './builders/log-client-data-builder';
import Store from "../../utils/store";
import {Check} from "@sparkware/app-utils";
import {isInsideAutomation} from "../../../../utils/automation-helpers";


export interface IElasticLoggerParams {
  serviceFactory: ServiceFactory,
  environment: string,
  storageUrl: string,
  eventData: IElasticEventData,
}

export default class ElasticLogger extends BaseObject implements IStructuredLogger {
  static CLASS = "ElasticLogger";

  private _serviceFactory:ServiceFactory;
  private _storageUrl;
  private _headerBuilder: LogEventHeaderBuilder;
  private _deviceDataBuilder: LogDeviceDataBuilder
  private _clientDataBuilder: LogClientDataBuilder;
  private _eventDataBuilder: LogEventDataBuilder;


  init(params:IElasticLoggerParams) {
    if (!params.serviceFactory) {
      this.throwArgumentExpected('38', 'init', 'serviceFactory');
    }

    this._headerBuilder = new LogEventHeaderBuilder(params.serviceFactory, params.environment);
    this._deviceDataBuilder = new LogDeviceDataBuilder();
    this._clientDataBuilder = new LogClientDataBuilder(params.serviceFactory);
    this._eventDataBuilder = new LogEventDataBuilder(params.serviceFactory, params.eventData);

    this._serviceFactory = params.serviceFactory;
    this._storageUrl = params.storageUrl;

    if(params.environment === "mirage") {
      const storageURLFromLS = Store.get("customLogstashURL", null);
      if(storageURLFromLS){
        this._storageUrl = storageURLFromLS;
      }
    }
  }


  setTarget(target): this {
    this._headerBuilder.setTarget(target);
    return this;
  }


  setCorrelationId(correlationID: string): this {
    this._headerBuilder.setCorrelationId(correlationID);
    return this;
  }

  setBusinessCorrelationID(businessCorrelationID: string): this {
    this._headerBuilder.setBusinessCorrelationID(businessCorrelationID);
    return this;
  }

  setSessionID(sessionID: string): this {
    this._headerBuilder.setSessionID(sessionID);
    return this;
  }


  setAppID(appID: string): this {
    this._headerBuilder.setAppID(appID);
    return this;
  }

  setProductPackageID(productPackageID: number): this {
    this._headerBuilder.setProductPackageID(productPackageID);
    return this;
  }


  setSequentialNumber(sequentialNumber: number): this {
    this._headerBuilder.setSequentialNumber(sequentialNumber);
    return this;
  }


  setEventType(eventName: string, ...eventTypes: EventType[]): this {
    this._headerBuilder.setEventType(eventName, ...eventTypes);
    return this;
  }

  addCategory(category: string): this {
    this._eventDataBuilder.addCategory(category);
    return this;
  }

  addAction(action: string): this {
    this._eventDataBuilder.addAction(action);
    return this;
  }

  addLabel(label: string): this {
    this._eventDataBuilder.addLabel(label);
    return this;
  }
  
  addValue(value: string): this {
    this._eventDataBuilder.addValue(value);
    return this;
  }

  addEventName(eventName: string): this {
    this._eventDataBuilder.addEventName(eventName);
    return this;
  }

  addCustomData(customData: object): this {
    this._eventDataBuilder.addCustomData(customData);
    return this;
  }

  _buildRequestPayload(level:string): ILogRequestPayload {
    let result: ILogRequestPayload = {
      eventHeader: this._headerBuilder.build(level),
      deviceData: this._deviceDataBuilder.build(),
      clientData: this._clientDataBuilder.build(),
      eventData: this._eventDataBuilder.build()
    };


    if(this._serviceFactory.oAuth2Service().isAuthorized()) {
      let decodedToken = this._serviceFactory.oAuth2Service().getToken();
      result.sessionData = {
        globalSessionID: parseInt(decodedToken.initialSessionId),
        playerSessionID: parseInt(decodedToken.sessionId)
      }

      let skinService = this._serviceFactory.skinService();
      result.userData = {
        cid: parseInt(decodedToken.cid),
        brand: skinService.getBrandName(),
        brandID: skinService.getBrandID()
      };
    }

    return result;
  }

  _send(level:string) {
    if(!isInsideAutomation()) {
      const payload = this._buildRequestPayload(level);
      this._serviceFactory.http2Service().postJSON(this._storageUrl, {body : payload, keepalive : true}).catch(err => {
        this.logger.error('Failed to send elastic event', err, payload);
      });
    }
  }

  info(target?:string) {
    if(!Check.isNullOrUndefined(target)){
      this._headerBuilder.setTarget(target);
    }
    this._send('information');
    return this;
  }

  warn() {
    this._send('warning');
    return this;
  }

  error() {
    this._send('error');
    return this;
  }

}
